import CmtCard from '@coremat/CmtCard';
import CmtCardContent from '@coremat/CmtCard/CmtCardContent';
import { Box, Button, Divider, Typography } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import makeStyles from '@mui/styles/makeStyles';
import { shouldShowQuantityPerPackageInfo } from 'services/Utilities/Utilities';

const actionButtonSx = {
  borderRadius: 2,
  maxHeight: 46,
  backgroundColor: '#fff',
  borderColor: '#000',
  borderWidth: 1,
  borderStyle: 'solid',
  boxShadow: 'none',
  '&:hover': {
    backgroundColor: '#E8E9EB',
    boxShadow: 'none',
  },
};

const useStyles = makeStyles(() => ({
  itemCardContent: () => ({
    padding: 0,
    position: 'relative',
    height: '100%',
  }),
  itemCard: () => ({
    borderRadius: 0,
    height: '100%',
  }),
  promoBadge: {
    position: 'absolute',
    top: 0,
    right: 0,
    backgroundColor: 'rgba(255, 221, 0, 0.88)',
    color: 'black',
    padding: '6px 24px',
    fontWeight: 'bold',
    fontSize: 18,
    zIndex: 1,
  },
}));

const DealerPortalMenuInventoryItemCard = ({
  product,
  view,
  onClick,
  onAddToCart,
  enableQuickShop = false,
  onQuickShop,
  isSetUpAction = false,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const isCard = view === 'card';
  const shouldUseSetUpAction = Boolean(enableQuickShop || isSetUpAction);
  const showQuickShopButton = shouldUseSetUpAction && Boolean(onQuickShop);
  const formattedDescription = (product?.shortDescription || '').split('\n').join('<br />');

  return (
    <CmtCard className={classes.itemCard}>
      <CmtCardContent className={classes.itemCardContent}>
        {product?.promotionPrice > 0 && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              right: 0,
              backgroundColor: 'rgba(255, 221, 0, 0.88)',
              color: 'black',
              px: 3,
              py: 0.75,
              zIndex: 1,
            }}>
            <Typography variant="h4">{t('onPromotion')}</Typography>
          </Box>
        )}

        <Box
          display="flex"
          flexDirection={isCard ? 'column' : 'row'}
          justifyContent="space-between"
          width="100%"
          height="100%">
          <Box minWidth="250px" onClick={onClick} display="flex" flexDirection={isCard ? 'column' : 'row'}>
            <Box textAlign="center">
              <Box
                component="img"
                src={product?.imgSrc}
                alt={product?.name || 'product-image'}
                sx={{
                  width: isCard ? '100%' : '25vw',
                  display: 'block',
                  height: '300px',
                  objectFit: 'contain',
                  cursor: onClick ? 'pointer' : 'default',
                }}
              />

              {isCard && <Divider sx={{ mt: 2, mx: 2 }} />}
            </Box>

            <Box
              display="flex"
              flexDirection="column"
              justifyContent="center"
              sx={{ mt: 2, px: 4, cursor: onClick ? 'pointer' : 'default' }}>
              <Typography variant="h3" sx={{ mb: 2, textTransform: 'uppercase' }}>
                {product?.name}
              </Typography>

              {product?.sku && (
                <Typography fontSize={12} color="text.secondary" sx={{ mb: 1 }}>
                  {product?.sku}
                </Typography>
              )}

              <Typography fontSize={12} sx={{ mb: 1 }}>
                <div
                  dangerouslySetInnerHTML={{
                    __html: formattedDescription,
                  }}
                />
              </Typography>
            </Box>
          </Box>

          <Box sx={{ pt: 4 }}>
            <Box
              display="flex"
              flexDirection={isCard ? 'row' : 'column'}
              justifyContent={isCard ? 'space-between' : 'flex-end'}
              sx={{ px: 4, height: '100%' }}>
              <Box onClick={onClick} sx={{ cursor: onClick ? 'pointer' : 'default', py: isCard ? 0 : 3 }}>
                {product?.price > 0 && (
                  <Typography color="text.secondary">
                    <Box sx={{ pt: 3 }} />
                    <Typography
                      variant="h4"
                      color={product?.promotionPrice ? 'text.secondary' : 'text.primary'}
                      sx={{
                        textDecorationLine: product?.promotionPrice ? 'line-through' : 'none',
                        fontWeight: product?.promotionPrice ? 'normal' : 'bold',
                      }}>
                      ${product?.price?.toFixed(2)} {product?.currencyName}
                    </Typography>

                    {shouldShowQuantityPerPackageInfo(product?.quantityPerPackage) && (
                      <Typography fontSize={12} color="text.secondary" sx={{ mb: 0 }}>
                        ({t('quantityPerPackageInfo', { quantityPerPackage: product?.quantityPerPackage })})
                      </Typography>
                    )}
                  </Typography>
                )}

                {product?.promotionPrice > 0 && (
                  <Box>
                    <Typography component="strong" variant="h3" color="error" fontWeight="bold">
                      ${product?.promotionPrice?.toFixed(2)} {product?.currencyName}
                    </Typography>

                    {product?.promotionQuantity != null && (
                      <Typography fontSize={12} color="text.secondary" sx={{ mb: -3, mt: 0 }}>
                        {t('promotionQuantityLeft', { count: product?.promotionQuantity })}
                      </Typography>
                    )}

                    {product?.inventoriedQuantityAvailable != null && (
                      <Typography fontSize={12} color="text.secondary" sx={{ mb: -3, mt: 0.5 }}>
                        {t('inventoriedQuantityLeft', { count: product?.inventoriedQuantityAvailable })}
                      </Typography>
                    )}
                  </Box>
                )}

                {product?.promotionPrice <= 0 && product?.inventoriedQuantityAvailable != null && (
                  <Typography fontSize={12} color="text.secondary" sx={{ mb: -3, mt: 0.5 }}>
                    {t('inventoriedQuantityLeft', { count: product?.inventoriedQuantityAvailable })}
                  </Typography>
                )}
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'flex-end',
                  flexDirection: isCard ? 'row' : 'column',
                  gap: 1,
                  pointerEvents: 'auto',
                }}>
                {showQuickShopButton && (
                  <Button
                    sx={actionButtonSx}
                    variant="contained"
                    color="secondary"
                    onClick={event => {
                      event.stopPropagation();
                      onQuickShop(event);
                    }}>
                    <Typography
                      sx={{
                        color: 'black !important',
                        fontSize: 22,
                        fontWeight: 800,
                        px: 0,
                        py: 2,
                        fontFamily: 'Trade Gothic Next LT Pro',
                        letterSpacing: '-0.02em',
                        mb: 1,
                      }}>
                      {t('quickshop')}
                    </Typography>
                  </Button>
                )}

                <Button
                  sx={actionButtonSx}
                  variant="contained"
                  color="secondary"
                  onClick={event => {
                    event.stopPropagation();
                    if (shouldUseSetUpAction) {
                      onClick && onClick();
                      return;
                    }
                    onAddToCart && onAddToCart(event);
                  }}
                  disabled={
                    shouldUseSetUpAction
                      ? false
                      : product?.isPromoLimitReached ||
                        (product?.isInventoriedLimitReached && !product?.inventoryItemOnceDepleted_Id)
                  }>
                  <Typography
                    sx={{
                      color: 'black !important',
                      fontSize: 22,
                      fontWeight: 800,
                      px: 0,
                      py: 2,
                      fontFamily: 'Trade Gothic Next LT Pro',
                      letterSpacing: '-0.02em',
                      mb: 1,
                    }}>
                    {shouldUseSetUpAction ? t('setUp') : t('addToCart')}
                  </Typography>
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
      </CmtCardContent>
    </CmtCard>
  );
};

export default DealerPortalMenuInventoryItemCard;
